import { Route, Camera, Search, MapPin, Lightbulb, TrendingUp } from 'lucide-react';
import Modal from '../../../../ui-components/Modal/Modal';
import Button from '../../../../ui-components/Button/Button';
import './LprJourneyFeatureIntro.css';

const LPR_JOURNEY_INTRO_SEEN_KEY = 'lprJourney_featureIntroSeen';

/**
 * LprJourneyFeatureIntro
 *
 * A welcoming modal that explains what LPR Journey is and how to use it.
 * Shown automatically on first visit; can be re-opened via the help button.
 * Designed for non-technical users and sales/business stakeholders.
 */
const LprJourneyFeatureIntro = ({ show, onClose, isFirstVisit = false }) => {
  const handleGotIt = () => {
    try {
      localStorage.setItem(LPR_JOURNEY_INTRO_SEEN_KEY, 'true');
    } catch { /* ignore */ }
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      position="center"
      backdrop
      keyboard
      className="lpr-intro-modal"
    >
      <Modal.Header closeButton onHide={onClose}>
        <span className="lpr-intro-header">
          <Route size={22} className="lpr-intro-header-icon" />
          Welcome to LPR Journey
        </span>
      </Modal.Header>

      <Modal.Body className="lpr-intro-body">
        {/* What is it */}
        <section className="lpr-intro-section">
          <h3 className="lpr-intro-section-title">
            <Lightbulb size={18} />
            What is LPR Journey?
          </h3>
          <p className="lpr-intro-desc">
            LPR Journey tracks how vehicles move between your cameras. When a vehicle passes multiple
            cameras, we build a journey: which cameras it visited and how long it took. This lets you
            understand traffic flow, spot delays, and see how people move through your site.
          </p>
        </section>

        {/* Why it matters (business value) */}
        <section className="lpr-intro-section">
          <h3 className="lpr-intro-section-title">
            <TrendingUp size={18} />
            Why it matters
          </h3>
          <ul className="lpr-intro-bullets">
            <li><strong>Operational insights</strong> — See which routes are busiest and where vehicles spend the most time</li>
            <li><strong>Spot anomalies</strong> — Quickly identify routes that are slower than normal</li>
            <li><strong>Search by plate</strong> — Look up any vehicle&apos;s full journey and timing</li>
            <li><strong>Easy to demonstrate</strong> — No technical background needed; ideal for stakeholders</li>
          </ul>
        </section>

        {/* How to use */}
        <section className="lpr-intro-section">
          <h3 className="lpr-intro-section-title">How to use it</h3>
          <div className="lpr-intro-steps">
            <div className="lpr-intro-step">
              <div className="lpr-intro-step-num">1</div>
              <div className="lpr-intro-step-content">
                <h4><MapPin size={14} /> Configure the map</h4>
                <p>Click <strong>Configure Map</strong> to place your cameras on a site map. Add a background image (e.g. parking layout) for context.</p>
              </div>
            </div>
            <div className="lpr-intro-step">
              <div className="lpr-intro-step-num">2</div>
              <div className="lpr-intro-step-content">
                <h4><Camera size={14} /> Camera Transitions</h4>
                <p>View traffic flow between camera pairs — how many vehicles move from A to B and how long they take.</p>
              </div>
            </div>
            <div className="lpr-intro-step">
              <div className="lpr-intro-step-num">3</div>
              <div className="lpr-intro-step-content">
                <h4><Search size={14} /> Vehicle Route Analysis</h4>
                <p>Search by licence plate to see that vehicle&apos;s complete journey, timing, and whether it looks normal or slow.</p>
              </div>
            </div>
          </div>
        </section>

        {isFirstVisit && (
          <p className="lpr-intro-tip">
            <strong>Tip:</strong> If you see &quot;Place cameras on the map&quot;, run through step 1 first. Once configured, data will appear as vehicles are detected.
          </p>
        )}
      </Modal.Body>

      <Modal.Footer className="lpr-intro-footer">
        <Button variant="primary" onClick={handleGotIt}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LprJourneyFeatureIntro;
export { LPR_JOURNEY_INTRO_SEEN_KEY };
